/**
 * Meta Pixel (fbq) — chargement paresseux après consentement analytics.
 * Ne pas importer ce module côté serveur.
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    __META_PIXEL_INITIALIZED_ID?: string;
  }
}

const SCRIPT_ID = 'meta-pixel-fbevents-js';
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

function installFbqStub(): void {
  if (typeof window === 'undefined' || window.fbq) {
    return;
  }
  const w = window as Window & { fbq?: (...args: unknown[]) => void };
  const fbqImpl = function (...args: unknown[]) {
    const self = fbqImpl as unknown as {
      callMethod?: (...a: unknown[]) => void;
      queue: unknown[][];
    };
    if (self.callMethod) {
      self.callMethod(...args);
    } else {
      self.queue.push(args);
    }
  } as unknown as (...args: unknown[]) => void;

  const self = fbqImpl as unknown as {
    callMethod?: (...a: unknown[]) => void;
    queue: unknown[][];
    push: typeof fbqImpl;
    loaded: boolean;
    version: string;
  };
  self.queue = [];
  self.push = fbqImpl as typeof self.push;
  self.loaded = false;
  self.version = '2.0';
  w.fbq = fbqImpl;
  if (!w._fbq) {
    w._fbq = fbqImpl;
  }
}

export function metaPixelSetConsent(granted: boolean): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  window.fbq('consent', granted ? 'grant' : 'revoke');
}

export type MetaTrackingPayload = {
  event_id: string;
  fbc?: string;
  fbp?: string;
  event_source_url?: string;
};

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function readMetaCookies(): { fbp?: string; fbc?: string } {
  return {
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
  };
}

export function createMetaEventId(prefix: string): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return `${prefix}${id}`;
}

export function buildMetaTrackingPayload(eventId: string): MetaTrackingPayload {
  const cookies = readMetaCookies();
  return {
    event_id: eventId,
    ...(cookies.fbc ? { fbc: cookies.fbc } : {}),
    ...(cookies.fbp ? { fbp: cookies.fbp } : {}),
    ...(typeof window !== 'undefined' && window.location?.href
      ? { event_source_url: window.location.href }
      : {}),
  };
}

/** Téléphone FR local (06…) → E.164 pour Meta (+336…). */
export function toMetaE164Phone(phone: string): string | undefined {
  const digits = phone.replace(/[^\d+]/g, '');
  if (!digits) {
    return undefined;
  }
  if (digits.startsWith('+')) {
    return digits;
  }
  if (digits.startsWith('33') && digits.length >= 11) {
    return `+${digits}`;
  }
  if (digits.startsWith('0') && digits.length >= 10) {
    return `+33${digits.slice(1)}`;
  }
  return digits.length >= 9 ? `+${digits}` : undefined;
}

function trackFbq(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  if (params && Object.keys(params).length > 0) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
}

export function metaPixelTrackPageView(): void {
  trackFbq('PageView');
}

export function metaPixelTrackViewContent(params: { content_name: string }): void {
  trackFbq('ViewContent', { content_name: params.content_name });
}

export function metaPixelTrackLead(params: {
  em: string;
  fn?: string;
  content_name?: string;
  content_category?: string;
  event_id: string;
}): void {
  const payload: Record<string, unknown> = {
    em: params.em.trim().toLowerCase(),
    eventID: params.event_id,
  };
  if (params.fn?.trim()) {
    payload.fn = params.fn.trim();
  }
  if (params.content_name) {
    payload.content_name = params.content_name;
  }
  if (params.content_category) {
    payload.content_category = params.content_category;
  }
  trackFbq('Lead', payload);
}

export function metaPixelTrackCompleteRegistration(params: {
  em: string;
  ph?: string;
  fn?: string;
  external_id?: string;
  event_id: string;
}): void {
  const payload: Record<string, unknown> = {
    em: params.em.trim().toLowerCase(),
    eventID: params.event_id,
  };
  const e164 = params.ph ? toMetaE164Phone(params.ph) : undefined;
  if (e164) {
    payload.ph = e164;
  }
  if (params.fn?.trim()) {
    payload.fn = params.fn.trim();
  }
  if (params.external_id) {
    payload.external_id = String(params.external_id);
  }
  trackFbq('CompleteRegistration', payload);
}

export function metaPixelTrackInitiateCheckout(params: {
  em: string;
  external_id?: string;
  event_id: string;
  content_name: string;
  value?: number;
  currency?: string;
}): void {
  const payload: Record<string, unknown> = {
    em: params.em.trim().toLowerCase(),
    eventID: params.event_id,
    content_name: params.content_name,
  };
  if (params.external_id) {
    payload.external_id = String(params.external_id);
  }
  if (params.value != null) {
    payload.value = params.value;
    payload.currency = params.currency ?? 'EUR';
  }
  trackFbq('InitiateCheckout', payload);
}

/**
 * Injecte fbevents.js (une fois) et exécute init + premier PageView (mis en file d'attente jusqu'au chargement du script).
 */
export function loadAndInitMetaPixel(pixelId: string): Promise<void> {
  if (typeof window === 'undefined' || !pixelId.trim()) {
    return Promise.resolve();
  }

  installFbqStub();

  if (window.__META_PIXEL_INITIALIZED_ID === pixelId) {
    metaPixelSetConsent(true);
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const scheduleInit = () => {
      if (window.__META_PIXEL_INITIALIZED_ID === pixelId) {
        return;
      }
      window.fbq!('init', pixelId);
      window.fbq!('track', 'PageView');
      metaPixelSetConsent(true);
      window.__META_PIXEL_INITIALIZED_ID = pixelId;
    };

    if (document.getElementById(SCRIPT_ID)) {
      scheduleInit();
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => {
      reject(new Error('Échec du chargement du script Meta Pixel'));
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    scheduleInit();
  });
}
